import { useEffect, useRef } from 'react'
import { useMessagesStore } from '@/shared/stores/messages.store'
import { useStatusStore } from '@/shared/stores/status.store'
import { trpc } from '@/shared/lib/trpc'
import { linkifyText } from '@/shared/lib/linkify'
import { markdownToHtml } from '@/shared/lib/markdown'
import { useStickToBottom } from '@/shared/lib/useStickToBottom'

// Type local pour les messages venant de React Query
interface Message {
  id: string
  session_id: string
  role: 'user' | 'assistant' | 'system'
  content: string
  content_type: 'text' | 'audio_transcript' | 'file_ref'
  status: 'sending' | 'streaming' | 'complete' | 'error'
  created_at: string
}

// messageSentSignal reutilise pour le repin stick-to-bottom (journal BDD #26422, extrait du
// fix Terminal #26277 via useStickToBottom) -- son ancien usage (piloter l'indicateur "Claude
// reflechit") a disparu, remplace par la derivation isThinking depuis le vrai statut de
// session (voir plus bas) ; le canal etait deja branche par ChatWidget.tsx, reutilise ici
// plutot que d'en creer un nouveau.
export function MessageList({ sessionId, messageSentSignal }: { sessionId: string; messageSentSignal?: number }) {
  const session = useMessagesStore(s => s.sessions[sessionId])
  const initSession = useMessagesStore(s => s.initSession)
  const bottomRef = useRef<HTMLDivElement>(null)
  const isInitialLoad = useRef(true)
  const { isPinnedToBottom, handleScroll, scrollToBottom } = useStickToBottom(bottomRef, messageSentSignal)

  // Statut agent réel relayé depuis le daemon (status:changed -> status.handler.ts). Seule
  // source de vérité pour l'indicateur "Claude réfléchit" : aucun timer, aucun état inventé.
  const agentStatus = useStatusStore(s => s.statuses[sessionId] ?? 'idle')

  // Ensure session is initialized in the store
  useEffect(() => {
    initSession(sessionId)
  }, [sessionId, initSession])

  const { data: messages = [] } = trpc.messages.list.useQuery(
    { sessionId, limit: 50 },
    { enabled: !!sessionId, staleTime: Infinity, refetchOnWindowFocus: false, refetchOnReconnect: false },
  )

  // Reset au changement de session
  useEffect(() => {
    isInitialLoad.current = true
  }, [sessionId])

  // Scroll instantané au premier chargement
  useEffect(() => {
    if (messages.length && isInitialLoad.current) {
      isInitialLoad.current = false
      bottomRef.current?.scrollIntoView({ behavior: 'instant' })
    }
  }, [messages.length])

  // Scroll pendant streaming -- SEULEMENT si pinne en bas (stick-to-bottom, journal BDD
  // #26422). isPinnedToBottom est dans les deps DELIBEREMENT (meme raisonnement que le fix
  // Terminal, journal BDD #26277) : sans lui, un retour manuel en bas ne recollerait qu'au
  // message suivant plutot qu'immediatement.
  //
  // behavior: 'instant', PAS 'smooth' (etait smooth avant ce fix) -- CHANGEMENT DELIBERE,
  // PROUVE empiriquement (journal BDD #26422, minilog Execution_preuve_smooth_vs_instant) :
  // une animation smooth deja en vol au moment ou l'utilisateur scroll vers le haut peut
  // continuer sa course et ramener la vue en bas malgre le lacher-prise, sur un simple geste
  // bref (dans le pire cas mesure : retour COMPLET en bas, pin re-arme -- reproduit exactement
  // le bug signale par Fabrice). instant elimine ce risque PAR CONSTRUCTION (scrollIntoView se
  // resout de facon synchrone, jamais d'animation en vol) -- verifie a 0px de "creep" vers le
  // bas sur tous les profils de geste testes, y compris le plus faible. Coherent avec Terminal
  // (deja en instant, deja valide par Fabrice).
  useEffect(() => {
    if (!isInitialLoad.current) {
      if (!isPinnedToBottom) return
      bottomRef.current?.scrollIntoView({ behavior: 'instant' })
    }
  }, [messages.length, session?.streamingContent, isPinnedToBottom])

  const streamingContent = session?.streamingContent ?? ''
  const isStreaming = session?.isStreaming ?? false

  // Indicateur "Claude réfléchit" : fonction pure du vrai statut de session, aucun timer,
  // aucun état intermédiaire mémorisé. Visible ssi le worker est réellement actif et qu'aucun
  // contenu streamé n'est déjà affiché (sinon l'indicateur ferait doublon avec la bulle
  // de streaming). Se cache automatiquement dès que le statut n'est plus 'active' (idle,
  // error, closed, aborting), y compris après un abort.
  const isThinking = agentStatus === 'active' && !isStreaming && !streamingContent

  return (
    <div
      className="message-list-container"
      onScroll={handleScroll}
      style={{
        flex: 1,
        overflowY: 'auto',
        padding: 'var(--space-4)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-5)',
      }}
    >
      {messages.length === 0 && !isStreaming && !streamingContent && (
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 'var(--space-3)',
            color: 'var(--color-text-muted)',
            fontFamily: 'var(--font-sans)',
            minHeight: 200,
          }}
        >
          <p style={{ fontSize: 'var(--text-sm)', margin: 0 }}>
            Ecrivez un message pour commencer la conversation.
          </p>
        </div>
      )}

      {messages.map(msg => (
        <MessageBubble key={msg.id} message={msg} />
      ))}

      {/* Thinking indicator */}
      {isThinking && (
        <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
          <div className="message-bubble" style={{
            background: 'transparent',
            border: '1px solid var(--color-border)',
            borderRadius: 16,
            padding: 16,
            maxWidth: '75%',
            minWidth: 200,
          }}>
            <div style={{
              fontFamily: 'var(--font-sans)',
              fontWeight: 700,
              fontSize: 16,
              color: 'var(--color-text-primary)',
              marginBottom: 12,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}>
              <span style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: 'var(--color-accent)',
                flexShrink: 0,
              }} />
              Claude
            </div>
            <div style={{
              background: 'var(--color-msg-content-bg)',
              borderRadius: 8,
              padding: '12px 16px',
              fontFamily: 'var(--font-serif)',
              fontWeight: 400,
              lineHeight: 1.7,
              fontSize: 'var(--text-base)',
              color: 'var(--color-text-muted)',
              fontStyle: 'italic',
            }}>
              Claude réfléchit
              <span className="thinking-dots">
                <span>.</span><span>.</span><span>.</span>
              </span>
            </div>
          </div>
        </div>
      )}

      {isStreaming && streamingContent && (
        <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
          <div className="message-bubble" style={{
            background: 'transparent',
            border: '1px solid var(--color-border)',
            borderRadius: 16,
            padding: 16,
            maxWidth: '75%',
            minWidth: 200,
          }}>
            <div style={{
              fontFamily: 'var(--font-sans)',
              fontWeight: 700,
              fontSize: 16,
              color: 'var(--color-text-primary)',
              marginBottom: 12,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}>
              <span style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: 'var(--color-accent)',
                flexShrink: 0,
              }} />
              Claude
            </div>
            <div
              className="markdown-body"
              style={{
                background: 'var(--color-msg-content-bg)',
                borderRadius: 8,
                padding: '12px 16px',
                fontFamily: 'var(--font-serif)',
                fontWeight: 400,
                lineHeight: 1.7,
                fontSize: 'var(--text-base)',
                color: 'var(--color-text-primary)',
                wordBreak: 'break-word' as const,
              }}
              dangerouslySetInnerHTML={{ __html: markdownToHtml(streamingContent) + '<span style="opacity:0.5">&#9612;</span>' }}
            />
          </div>
        </div>
      )}
      <div ref={bottomRef} />

      {/* Bouton "aller en bas" -- meme mecanique que Terminal (sticky, pas absolute :
          .message-list-container est le conteneur scrollable lui-meme -- un enfant absolute
          s'y positionnerait par rapport au contenu total, invisible precisement quand on a
          remonte, journal BDD #26277/#26422). Style INLINE (pas de classe CSS dediee) pour
          rester coherent avec le reste de ce fichier, qui n'utilise que des style={{}} --
          palette/typo adaptees a l'esthetique Discussion (--font-sans, tons de la bulle de
          chat) plutot que le monospace du Terminal. Position sticky verifiee dans un
          conteneur flex-column (different du bloc de Terminal) via harnais isole -- bouton
          reste dans les bornes visibles, ni ecrase ni etire par align-items:stretch (journal
          BDD #26422). */}
      {!isPinnedToBottom && (
        <button
          type="button"
          className="message-list-scroll-to-bottom"
          onClick={scrollToBottom}
          aria-label="Aller en bas de la conversation"
          style={{
            position: 'sticky',
            bottom: 12,
            display: 'block',
            width: 'fit-content',
            margin: '8px auto 0',
            background: 'var(--color-bg-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-full)',
            boxShadow: 'var(--shadow-sm)',
            color: 'var(--color-text-secondary)',
            cursor: 'pointer',
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--text-xs)',
            fontWeight: 600,
            padding: '6px 14px',
          }}
        >
          {'↓ Reprendre en bas'}
        </button>
      )}
    </div>
  )
}

function MessageBubble({ message }: { message: Message }) {
  const isUser = message.role === 'user'
  const isError = message.status === 'error'
  const speakerName = isUser ? 'Fabrice' : 'Claude'

  return (
    <div style={{
      display: 'flex',
      justifyContent: isUser ? 'flex-end' : 'flex-start',
    }}>
      <div className="message-bubble" style={{
        background: 'transparent',
        border: '1px solid var(--color-border)',
        borderRadius: 16,
        padding: 16,
        maxWidth: '75%',
        minWidth: 200,
        opacity: message.status === 'sending' ? 0.7 : 1,
      }}>
        {/* Speaker name with colored dot */}
        <div style={{
          fontFamily: 'var(--font-sans)',
          fontWeight: 700,
          fontSize: 16,
          color: 'var(--color-text-primary)',
          marginBottom: 12,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}>
          <span style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: isUser ? '#2ecc71' : 'var(--color-accent)',
            flexShrink: 0,
          }} />
          {speakerName}
        </div>

        {/* Message content */}
        <div style={{
          background: 'var(--color-msg-content-bg)',
          borderRadius: 8,
          padding: '12px 16px',
          fontFamily: 'var(--font-serif)',
          fontWeight: 400,
          lineHeight: 1.7,
          fontSize: 'var(--text-base)',
          color: 'var(--color-text-primary)',
          wordBreak: 'break-word' as const,
          border: isError ? '1px solid var(--color-error)' : 'none',
        }}>
          {isUser ? (
            // Messages utilisateur : texte brut avec liens cliquables
            <div style={{ whiteSpace: 'pre-wrap' }}>
              {linkifyText(message.content)}
            </div>
          ) : (
            // Messages assistant : rendu Markdown
            <div
              className="markdown-body"
              dangerouslySetInnerHTML={{ __html: markdownToHtml(message.content) }}
            />
          )}
          {isError && (
            <div style={{
              fontSize: 'var(--text-xs)',
              color: 'var(--color-error)',
              marginTop: 'var(--space-1)',
            }}>
              Erreur d&apos;envoi
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// Export du type Message pour compatibilité (utilisé par TerminalView)
export type { Message }
